import firebaseKit, { db } from "../firebase/firebaseKit";
import AuthKit from "../AuthKit";
import PublicChatMessage from "./PublicChatMessage";
import PrivateChatMessage from "./PrivateChatMessage";
import ChatMessageCollection from "./ChatMessageCollection";
import CorporateChatMessage from "./CorporateChatMessage";
import { getRef, refs } from "../firebase/firebaseRefKit";
import {
  onValue,
  off,
  onChildAdded,
  onChildRemoved,
  onChildChanged,
  get,
  query,
  limitToLast,
  orderByChild,
  set,
  update,
} from "firebase/database";

const authKit = new AuthKit();

const shortcuts = {
  corporateInboxes:
    refs["corporate-messages"].$organizationSlug.$eventSlug.inboxes,
};

export default class ChatMessageKit {
  // PUBLIC CHAT

  static getMutedList({ eventSlug = "", onChange = () => { } }) {
    const ref = getRef(refs["public-messages"].$eventSlug.muted, { eventSlug });
    onValue(ref, (snapshot) => {
      onChange(snapshot);
    });
  }

  // PRIVATE CHAT
  static getUserInbox({ eventUserId }) {
    // const ref = firebase
    //   .database()
    //   .ref(`private-messages/user-inboxes/${eventUserId}`);
    const ref = getRef(refs["private-messages"]["user-inboxes"].$eventUserId, {
      eventUserId,
    });
    if (ref) {
      return {
        getInitial(onChange = () => { }) {
          if (ref) {
            onValue(
              ref,
              (snapshot) => {
                onChange(snapshot);
              },
              { onlyOnce: true }
            );
          }
        },
        onChildAdded(onChange = () => { }) {
          const latestRef = query(ref, limitToLast(1));
          const orderedRef = query(latestRef, orderByChild("lastActivity"));
          onChildAdded(orderedRef, (snapshot) => {
            onChange(snapshot);
          });
        },
        onChildChanged(onChange = () => { }) {
          onChildChanged(ref, (snapshot) => {
            onChange(snapshot);
          });
        },
        off() {
          off(ref);
        },
      }
    };
  }

  static async createPrivateChatroom({ usersIdToInvite, eventSlug }) {
    const response = await authKit.doPost(
      `/socials/chats/private-messages/${eventSlug}/`,
      { invitees: usersIdToInvite }
    );
    return response.data;
  }

  static async createPrivateVideoroom({ eventSlug, inboxUUID }) {
    const response = await authKit.doPost(
      `/socials/chats/private-messages/${eventSlug}/videorooms/${inboxUUID}/`,
      {}
    );
    return response.data.videoRoomSlug;
  }

  static listenToVideoroomSettings({ inboxUUID = "", onChange = () => { } }) {
    const ref = getRef(
      refs["private-messages"].inboxes.$inboxUUID.settings.videoroom,
      {
        inboxUUID,
      }
    );
    if (ref) {
      onValue(ref, (snapshot) => {
        onChange(snapshot);
      });

      return () => off(ref);
    }
  }

  static listenToLastActive({ inboxUUID = "", id = "", onChange = () => { } }) {
    const ref = getRef(
      refs["private-messages"].inboxes.$inboxUUID.settings.members.$eventUserId,
      { inboxUUID, eventUserId: id }
    );
    if (ref) {
      onValue(ref, (snapshot) => {
        onChange(snapshot);
      });

      return () => off(ref);
    }
  }

  static setLastActive({
    inboxUUID = "",
    id = "",
    lastActive = firebaseKit.util.getServerTimestamp(),
  }) {
    const ref = getRef(
      refs["private-messages"].inboxes.$inboxUUID.settings.members.$eventUserId,
      { inboxUUID, eventUserId: id }
    );

    if (ref) set(ref, { lastActive });
  }

  static setUserInboxLastActivity({
    inboxUUID = "",
    isArchived,
    id = "",
    lastActive = firebaseKit.util.getServerTimestamp(),
  }) {
    const ref = getRef(
      refs["private-messages"]["user-inboxes"].$eventUserId.$inboxUUID,
      {
        eventUserId: id,
        inboxUUID,
      }
    );
    if (ref) update(ref, { lastActivity: lastActive, isArchived: isArchived });
  }

  static setUserInboxArchived({ inboxUUID = "", isArchived, id = "" }) {
    const ref = getRef(
      refs["private-messages"]["user-inboxes"].$eventUserId.$inboxUUID
        .isArchived,
      {
        inboxUUID,
        eventUserId: id,
      }
    );
    if (ref) set(ref, isArchived);
  }

  // CORPORATE CHAT
  static async getInitialCorporateMessageSettings({
    organizationSlug,
    eventSlug,
    onValue: onChange = () => { },
  }) {
    const ref = getRef(
      refs["corporate-messages"].$organizationSlug.$eventSlug.settings.public[
      "initial-message"
      ],
      { organizationSlug, eventSlug }
    );
    if (ref) {
      onValue(
        ref,
        (snapshot) => {
          onChange(snapshot);
        },
        { onlyOnce: true }
      );
    }
  }

  static corporateChatExists({
    organizationSlug,
    eventUserId,
    eventSlug,
    pageSlug,
  }) {
    const ref = getRef(
      refs["corporate-messages"].$organizationSlug.$eventSlug.inboxes.$pageSlug
        .$eventUserId,
      {
        organizationSlug,
        eventSlug,
        pageSlug,
        eventUserId,
      }
    );
    if (ref) {
      return new Promise((resolve) => {
        onValue(ref, (snapshot) => resolve(snapshot.exists()), {
          onlyOnce: true,
        });
      });
    }
  }

  static async createCorporateChatroom({
    eventUserId,
    organizationSlug,
    pageSlug,
    eventSlug,
    corporateOrganization,
    initialMessage,
  }) {
    let payload = {};

    if (initialMessage) {
      payload = new CorporateChatMessage({
        urlParams: {
          organizationSlug,
          pageSlug,
          eventSlug,
          evntUserId: eventUserId,
        },
        payload: {
          author: {
            displayName: corporateOrganization.name,
          },
          isAgent: true,
          message: initialMessage,
        },
      }).toObject();
    }

    const response = await authKit.doPost(
      `/socials/chats/corporate-messages/${organizationSlug}/${eventSlug}/inboxes/${pageSlug}/`,
      payload
    );
    return response.data;
  }

  static async createCorporateVideoroom({
    organizationSlug,
    pageSlug,
    eventUserId,
    eventSlug,
  }) {
    // console.log(`/socials/chats/corporate-messages/${organizationSlug}/videorooms/${eventSlug}/${pageSlug}/${eventUserId}/`)
    const response = await authKit.doPost(
      `/socials/chats/corporate-messages/${organizationSlug}/${eventSlug}/videorooms/${pageSlug}/${eventUserId}/`,
      {}
    );
    return response.data.videoRoomSlug;
  }

  static getCorporateChatrooms({
    organizationSlug = "",
    eventSlug = "",
    pageSlug = "",
  }) {
    const ref = getRef(
      refs["corporate-messages"].$organizationSlug.$eventSlug.pages.$pageSlug,
      {
        organizationSlug,
        eventSlug,
        pageSlug,
      }
    );

    // ref.on('value', (snapshot) => { onChange(snapshot) })
    if (ref) {
      return {
        getInitial(onChange = () => { }) {
          onValue(
            ref,
            (snapshot) => {
              onChange(snapshot);
            },
            { onlyOnce: true }
          );
        },
        onChildAdded(onChange = () => { }) {
          //Changed limitToLast to 10, so we have some margin in case listener breaks
          //initially and needs to be rebound. This will download the 10 latest chats.
          //Not ideal, but likely a better option than re-downloading the entire list again.
          onChildAdded(ref, (snapshot) => {
            onChange(snapshot);
          });
        },
        onChildChanged(onChange = () => { }) {
          onChildChanged(ref, (snapshot) => {
            onChange(snapshot);
          });
        },
        off() {
          off(ref);
        },
        ref,
      };
    }
  }

  static getCorporateInboxes({
    organizationSlug = "",
    eventSlug = "",
    pageSlug = "",
  }) {
    const ref = getRef(
      refs["corporate-messages"].$organizationSlug.$eventSlug.inboxes.$pageSlug,
      {
        organizationSlug,
        eventSlug,
        pageSlug,
      }
    );
    // const url = `corporate-messages/${organizationSlug}/${eventSlug}/inboxes/${pageSlug}`;

    // const ref = firebase.database().ref(url);
    if (ref) {
      return {
        getInitial(onChange = () => { }) {
          onValue(
            ref,
            (snapshot) => {
              onChange(snapshot);
            },
            { onlyOnce: true }
          );
        },
        onChildAdded(onChange = () => { }) {
          //Changed limitToLast to 10, so we have some margin in case listener breaks
          //initially and needs to be rebound. This will download the 10 latest chats.
          //Not ideal, but likely a better option than re-downloading the entire list again.
          const limit = query(ref, limitToLast(10));
          const ordered = query(limit, orderByChild("lastActivity"));

          onChildAdded(ordered, (snapshot) => {
            onChange(snapshot);
          });
        },
        onChildChanged(onChange = () => { }) {
          onChildChanged(ref, (snapshot) => {
            onChange(snapshot);
          });
        },
        off() {
          off(ref);
        },
        ref,
      };
    }
  }

  static getCorporateChatTitle({
    organizationSlug = "",
    eventSlug = "",
    onChange = () => { }
  }) {
    const ref = getRef(
      refs["corporate-messages"].$organizationSlug.$eventSlug.settings.public[
        "initial-message"
      ].chatTitle,
      {
        organizationSlug,
        eventSlug,
      }
    );
    if (ref) {
      onValue(ref, (snapshot) => onChange(snapshot), { onlyOnce: true })
      return () => off(ref)
    }
  }

  static setCorporateChatTitle({
    organizationSlug = "",
    eventSlug = "",
    chatTitle = "",
  }) {
    const ref = getRef(
      refs["corporate-messages"].$organizationSlug.$eventSlug.settings.public[
        "initial-message"
      ].chatTitle,
      {
        organizationSlug,
        eventSlug,
      }
    );
    if (ref) set(ref, chatTitle);
  }

  static setCorporateWelcomeMessage({
    organizationSlug = "",
    eventSlug = "",
    message = "",
  }) {
    const ref = getRef(
      refs["corporate-messages"].$organizationSlug.$eventSlug.settings.public[
        "initial-message"
      ].message,
      {
        organizationSlug,
        eventSlug,
      }
    );
    if (ref) set(ref, message);
  }

  static setCorporateOfflineMessage({
    organizationSlug = "",
    eventSlug = "",
    message = "",
  }) {
    const ref = getRef(
      refs["corporate-messages"].$organizationSlug.$eventSlug.settings.public[
        "initial-message"
      ].offlineMessage,
      {
        organizationSlug,
        eventSlug,
      }
    );
    if (ref) set(ref, message);
  }

  static getCorporateChatOnline({
    organizationSlug = "",
    eventSlug = "",
    onChange = () => { }
  }) {
    const ref = getRef(
      refs["corporate-messages"].$organizationSlug.$eventSlug.settings.public[
        "initial-message"
      ].online,
      {
        organizationSlug,
        eventSlug,
      }
    );

    if (ref) {
      onValue(ref, (snapshot) => onChange(snapshot), { onlyOnce: true })
      return () => off(ref)
    }
  }

  static setCorporateChatOnline({
    organizationSlug = "",
    eventSlug = "",
    online = false,
  }) {
    const ref = getRef(
      refs["corporate-messages"].$organizationSlug.$eventSlug.settings.public[
        "initial-message"
      ].online,
      {
        organizationSlug,
        eventSlug,
      }
    );
    if (ref) set(ref, online);
  }

  static getCorporateAutoMessageSettings({
    organizationSlug = "",
    eventSlug = "",
    onChange = () => { },
  }) {
    // console.log(firebase.auth().currentUser)
    const ref = getRef(
      refs["corporate-messages"].$organizationSlug.$eventSlug.settings.public[
      "initial-message"
      ],
      {
        organizationSlug,
        eventSlug,
      }
    );
    if (ref) {
      onValue(ref, (snapshot) => onChange(snapshot));
      return () => off(ref);
    }
  }

  static getCorporateAgentUserSettings({
    organizationSlug = "",
    eventSlug = "",
    userId = "",
    onChange = () => { },
  }) {
    const ref = getRef(
      refs["corporate-messages"].$organizationSlug.$eventSlug.settings.private
        .agents.$agentId,
      {
        organizationSlug,
        eventSlug,
        agentId: userId,
      }
    );
    if (ref) {
      onValue(ref, (snapshot) => onChange(snapshot));
      return () => off(ref);
    }
  }

  static setCorporateAgentUserDisplayNameSetting({
    organizationSlug = "",
    eventSlug = "",
    userId = "",
    displayName = "",
  }) {
    const ref = getRef(
      refs["corporate-messages"].$organizationSlug.$eventSlug.settings.private
        .agents.$agentId.displayName,
      {
        organizationSlug,
        eventSlug,
        agentId: userId,
      }
    );
    if (ref) set(ref, displayName);
  }

  static setCorporateAgentUserTitleSetting({
    organizationSlug = "",
    eventSlug = "",
    userId = "",
    title = "",
  }) {
    const ref = getRef(
      refs["corporate-messages"].$organizationSlug.$eventSlug.settings.private
        .agents.$agentId.title,
      {
        organizationSlug,
        eventSlug,
        agentId: userId,
      }
    );
    if (ref) set(ref, title);
  }

  static setCorporateChatIsTrashed({
    organizationSlug = "",
    eventSlug = "",
    pageSlug = "",
    eventUserId = "",
    isTrashed = false,
  }) {
    const ref = getRef(
      refs["corporate-messages"].$organizationSlug.$eventSlug.pages.$pageSlug
        .$eventUserId.settings.isTrashed,
      {
        organizationSlug,
        eventSlug,
        pageSlug,
        eventUserId,
      }
    );
    if (ref) set(ref, isTrashed);
  }

  static getCorporateChatIsTrashed({
    organizationSlug = "",
    eventSlug = "",
    pageSlug = "",
    eventUserId = "",
    onChange = () => { },
  }) {
    const ref = getRef(
      refs["corporate-messages"].$organizationSlug.$eventSlug.pages.$pageSlug
        .$eventUserId.settings.isTrashed,
      {
        organizationSlug,
        eventSlug,
        pageSlug,
        eventUserId,
      }
    );
    if (ref) {
      onValue(ref, (snapshot) => onChange(snapshot));
      return () => off(ref);
    }
  }

  static setCorporateChatClaimedBy({
    organizationSlug = "",
    eventSlug = "",
    pageSlug = "",
    eventUserId = "",
    claimedBy = false,
  }) {
    const ref = getRef(
      refs["corporate-messages"].$organizationSlug.$eventSlug.pages.$pageSlug
        .$eventUserId.settings.claimedBy,
      {
        organizationSlug,
        eventSlug,
        pageSlug,
        eventUserId,
      }
    );
    if (ref) set(ref, claimedBy);
  }

  static getCorporateChatClaimedBy({
    organizationSlug = "",
    eventSlug = "",
    pageSlug = "",
    eventUserId = "",
    onChange = () => { },
  }) {
    const ref = getRef(
      refs["corporate-messages"].$organizationSlug.$eventSlug.pages.$pageSlug
        .$eventUserId.settings.claimedBy,
      {
        organizationSlug,
        eventSlug,
        pageSlug,
        eventUserId,
      }
    );
    if (ref) {
      onValue(ref, (snapshot) => onChange(snapshot));
      return () => off(ref);
    }
  }

  static setCorporateAgentLastActive({
    organizationSlug = "",
    eventSlug = "",
    pageSlug = "",
    eventUserId = "",
    lastActive = firebaseKit.util.getServerTimestamp(),
  }) {
    const ref = getRef(
      refs["corporate-messages"].$organizationSlug.$eventSlug.inboxes.$pageSlug
        .$eventUserId.settings.agent.lastActive,
      {
        organizationSlug,
        eventSlug,
        pageSlug,
        eventUserId,
      }
    );
    if (ref) set(ref, lastActive);
  }

  static getCorporateAgentLastActive({
    organizationSlug = "",
    eventSlug = "",
    pageSlug = "",
    eventUserId = "",
    onChange = () => { },
  }) {
    const ref = getRef(
      refs["corporate-messages"].$organizationSlug.$eventSlug.inboxes.$pageSlug
        .$eventUserId.settings.agent.lastActive,
      {
        organizationSlug,
        eventSlug,
        pageSlug,
        eventUserId,
      }
    );
    if (ref) {
      onValue(ref, (snapshot) => onChange(snapshot));
      return () => off(ref);
    }
  }

  static setCorporateClientLastActive({
    organizationSlug = "",
    eventSlug = "",
    pageSlug = "",
    eventUserId = "",
    lastActive = firebaseKit.util.getServerTimestamp(),
  }) {
    const ref = getRef(
      refs["corporate-messages"].$organizationSlug.$eventSlug.inboxes.$pageSlug
        .$eventUserId.settings.client.lastActive,
      {
        organizationSlug,
        eventSlug,
        pageSlug,
        eventUserId,
      }
    );
    if (ref) set(ref, lastActive);
  }

  static getCorporateClientLastActive({
    organizationSlug = "",
    eventSlug = "",
    pageSlug = "",
    eventUserId = "",
    onChange = () => { },
  }) {
    const ref = getRef(
      refs["corporate-messages"].$organizationSlug.$eventSlug.inboxes.$pageSlug
        .$eventUserId.settings.client.lastActive,
      {
        organizationSlug,
        eventSlug,
        pageSlug,
        eventUserId,
      }
    );
    if (ref) {
      onValue(ref, (snapshot) => onChange(snapshot));
      return () => off(ref);
    }
  }

  static setCorporateClientAvailability({
    organizationSlug = "",
    eventSlug = "",
    pageSlug = "",
    eventUserId = "",
    availability = 0,
  }) {
    const ref = getRef(
      refs["corporate-messages"].$organizationSlug.$eventSlug.availability
        .$pageSlug.$eventUserId.client.availability,
      {
        organizationSlug,
        eventSlug,
        pageSlug,
        eventUserId,
      }
    );
    if (ref) set(ref, availability);
  }

  static getCorporateClientAvailability({
    organizationSlug = "",
    eventSlug = "",
    pageSlug = "",
    eventUserId = "",
    onChange = () => { },
  }) {
    const ref = firebase
      .database()
      .ref(
        `corporate-messages/${organizationSlug}/${eventSlug}/inboxes/${pageSlug}/${eventUserId}/settings/client/availability`
      );

    ref.on("value", (snapshot) => onChange(snapshot));

    return () => off(ref);
  }

  static setCorporateAgentLatestActivity({
    organizationSlug = "",
    eventSlug = "",
    agentId = "",
    latestActivity = firebaseKit.util.getServerTimestamp(),
  }) {
    const ref = getRef(
      refs["corporate-messages"].$organizationSlug.$eventSlug.settings.private
        .agents.$agentId.latestActivity,
      {
        organizationSlug,
        eventSlug,
        agentId,
      }
    );
    if (ref) set(ref, latestActivity);
  }

  static getCorporateAgentLatestActivity({
    organizationSlug = "",
    eventSlug = "",
    agentId = "",
    onChange = () => { },
  }) {
    // console.log(`corporate-messages/${organizationSlug}/${eventSlug}/inboxes/${pageSlug}/${eventUserId}/settings/client/Availability`)
    const ref = getRef(
      refs["corporate-messages"].$organizationSlug.$eventSlug.settings.private
        .agents.$agentId.latestActivity,
      {
        organizationSlug,
        eventSlug,
        agentId,
      }
    );
    if (ref) {
      onValue(ref, (snapshot) => onChange(snapshot));
      return () => off(ref);
    }
  }
  static setCorporateAgentAvailability({
    organizationSlug = "",
    eventSlug = "",
    agentId = "",
    availability = false,
  }) {
    const ref = getRef(
      refs["corporate-messages"].$organizationSlug.$eventSlug.settings.private
        .agents.$agentId.availability,
      {
        organizationSlug,
        eventSlug,
        agentId,
      }
    );
    if (ref) set(ref, availability);
  }

  static getCorporateAgentAvailability({
    organizationSlug = "",
    eventSlug = "",
    agentId = "",
    onChange = () => { },
  }) {
    const ref = getRef(
      refs["corporate-messages"].$organizationSlug.$eventSlug.settings.private
        .agents.$agentId.availability,
      {
        organizationSlug,
        eventSlug,
        agentId,
      }
    );
    if (ref) {
      onValue(ref, (snapshot) => onChange(snapshot));
      return () => off(ref);
    }
  }

  static getCorporateVideoroomSettings({
    organizationSlug = "",
    eventSlug = "",
    pageSlug = "",
    eventUserId = "",
    onChange = () => { },
  }) {
    const ref = getRef(
      refs["corporate-messages"].$organizationSlug.$eventSlug.inboxes.$pageSlug
        .$eventUserId.settings.videoroom,
      {
        organizationSlug,
        eventSlug,
        pageSlug,
        eventUserId,
      }
    );
    if (ref) {
      onValue(ref, (snapshot) => {
        onChange(snapshot);
      });
      return () => off(ref);
    }
  }

  static getCorporateInboxTabTitle({
    organizationSlug = "",
    eventSlug = "",
    pageSlug = "",
    eventUserId = "",
    onChange = () => { },
  }) {
    const ref = getRef(
      refs["corporate-messages"].$organizationSlug.$eventSlug.inboxes.$pageSlug
        .$eventUserId.settings.agent.tabTitle,
      {
        organizationSlug,
        eventSlug,
        pageSlug,
        eventUserId,
      }
    );
    if (ref) {
      onValue(ref, (snapshot) => {
        onChange(snapshot);
      });
      return () => off(ref);
    }
  }

  static setCorporateInboxLatestActivity({
    organizationSlug = "",
    eventSlug = "",
    pageSlug = "",
    eventUserId = "",
    latestActivity = 0,
  }) {
    const ref = getRef(
      refs["corporate-messages"].$organizationSlug.$eventSlug.inboxes.$pageSlug
        .$eventUserId.settings.latestActivity,
      {
        organizationSlug,
        eventSlug,
        pageSlug,
        eventUserId,
      }
    );
    if (ref) set(ref, latestActivity);
  }

  static setCorporateClientIsBusy({
    organizationSlug = "",
    eventSlug = "",
    pageSlug = "",
    eventUserId = "",
    isBusy = false,
  }) {
    const ref = getRef(
      refs["corporate-messages"].$organizationSlug.$eventSlug.pages.$pageSlug
        .$eventUserId.settings.latestActivity,
      {
        organizationSlug,
        eventSlug,
        pageSlug,
        eventUserId,
      }
    );
    if (ref) set(ref, isBusy);
  }

  static getCorporateAvailability({
    organizationSlug = "",
    eventSlug = "",
    pageSlug = "",
  }) {
    const ref = getRef(
      refs["corporate-messages"].$organizationSlug.$eventSlug.availability
        .$pageSlug,
      {
        organizationSlug,
        eventSlug,
        pageSlug,
      }
    );
    if (ref) {
      return {
        getInitial(onChange = () => { }) {
          onValue(
            ref,
            (snapshot) => {
              onChange(snapshot);
            },
            { onlyOnce: true }
          );
        },
        onChildAdded(onChange = () => { }) {
          onChildAdded(ref, (snapshot) => {
            onChange(snapshot);
          });
        },
        onChildChanged(onChange = () => { }) {
          onChildChanged(ref, (snapshot) => {
            onChange(snapshot);
          });
        },
        off() {
          off(ref);
        },
        ref,
      };
    }
  }

  static async createCorporateChatMessageLog({
    kind = ChatMessageEnums.TEXT,
    recipient = "",
    organizationSlug = "",
    pageSlug = "",
    eventSlug = "",
  }) {
    const response = await authKit.doPost(
      `/socials/chats/corporate-messages/${organizationSlug}/${eventSlug}/messages/${pageSlug}/`,
      {
        kind,
        recipient,
      }
    );
    return response.data;
  }
}

const ChatMessageEnums = {
  TEXT: 0,
  VIDEO: 1,
  ANNONCEMENT: 2,
};

const ChatTypeEnums = {
  PRIVATE: "private",
  CORPORATE: "corporate",
  PUBLIC: "public",
};

export {
  PublicChatMessage,
  PrivateChatMessage,
  CorporateChatMessage,
  ChatMessageCollection,
  ChatMessageEnums,
  ChatTypeEnums,
};
